import { useMemo } from 'react';
import { createInfiniteQuery } from 'react-query-kit';
import { keepPreviousData } from '@tanstack/react-query';

import type { QuizsetSearchReq } from '@/api/gen/_model/quizsetSearchReq';
import type { SearchResponseListSearchQuizsetRes } from '@/api/gen/_model/searchResponseListSearchQuizsetRes';
import { searchMyQuizsets } from '@/api/gen/quiz-user/quiz-user.gen';
import { useSearchQuizQuery } from '@/module/public/hooks/useSearchQuizQuery';

export const useMyQuizes = () => {
  const { searchText, order } = useSearchQuizQuery();

  const result = useFetchMyQuizes({
    enabled: true,
    variables: {
      limit: 20,
      order,
      categoryFilters: {
        criteria: {
          ...(searchText && {
            searchText: searchText,
            searchType: 'TITLE',
          }),
        },
      },
    },
  });

  const flatData = useMemo(
    () => result.data?.pages.flatMap(page => page.data ?? []) ?? [],
    [result.data],
  );

  return {
    ...result,
    flatData,
    hasNextPage: result.hasNextPage,
    fetchNextPage: result.fetchNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
  };
};

export const useFetchMyQuizes = createInfiniteQuery<
  SearchResponseListSearchQuizsetRes,
  Omit<QuizsetSearchReq, 'page'>
>({
  queryKey: ['quizsets/search'],
  fetcher: async (variables, pageParam) => {
    return searchMyQuizsets({
      ...variables,
      page: pageParam.pageParam,
    });
  },
  getNextPageParam: lastPage => {
    if (!lastPage?.currentPage || !lastPage?.totalPages) {
      return undefined;
    }

    return lastPage.currentPage < lastPage.totalPages
      ? lastPage.currentPage + 1
      : undefined;
  },
  initialPageParam: 1,
  placeholderData: keepPreviousData,
});
